import { Account, Credentials } from '@/api/generated'
import { AuthenticationModule } from '@/authentication/store/authentication-module'
import { Injector } from '@/config/dependency-injection'
import { Role } from '@/shared/models/role'
import { toMap } from '@/shared/utils/map'
import { VueRouter } from 'vue-router/types/router'
import { RouterToken } from '@/config/injection-tokens'
import { CLEAR_AUTHENTICATION } from '@/authentication/store/types'
import { TokenHolder } from '@/shared/utils/token-holder'
import { NotificationService } from '@/shared/services/notification-service'
import { Anonymous } from '@/authentication/models/Anonymous'

export class AuthenticationService {
  private authenticationModule: AuthenticationModule

  private initialized: boolean = false

  constructor() {
    this.authenticationModule = Injector.get<AuthenticationModule>(AuthenticationModule)
  }

  public authenticatedUser(): Account {
    return this.authenticationModule.authenticatedUser
  }

  public clearAuthentication(options: { goHome: boolean }) {
    if (options.goHome === true) {
      const router = Injector.get<VueRouter>(RouterToken)

      router.push({
        name: 'home',
      })
    }

    const authenticationModule = Injector.get(AuthenticationModule)

    authenticationModule[CLEAR_AUTHENTICATION]()
  }

  public ensureAuthenticationIsValid(options: { goHome: boolean }) {
    if (this.isAuthenticated()) {
      const tokenHolder = Injector.get(TokenHolder)

      if (tokenHolder.ensureTokenIsValid() === false) {
        this.clearAuthentication(options)

        const notificationService = Injector.get(NotificationService)

        notificationService.showError('auth.logged_out_because_token_is_no_longer_valid')
      }
    }
  }

  public hasRoles(requiredRoles: Role[]): boolean {
    let hasRequiredRoles = false

    const authenticatedUser = this.authenticatedUser()

    if (authenticatedUser) {
      const userRoles = toMap<string>(authenticatedUser.roles, (item, map) => (map[item] = item))

      hasRequiredRoles = requiredRoles.reduce(
        (hasRoles: boolean, role: Role) => hasRoles && userRoles[role] !== undefined,
        true
      )
    }

    return hasRequiredRoles
  }

  public initialize(): Promise<Account | undefined> {
    return new Promise((resolve, reject) => {
      if (this.initialized) {
        resolve(this.authenticatedUser())
      } else {
        this.refreshUser()
          .then(resolve)
          .catch(reject)
          .finally(() => (this.initialized = true))
      }
    })
  }

  public isAuthenticated(): boolean {
    return this.authenticatedUser()?.id !== Anonymous.id
  }

  public login(credentials: Credentials): Promise<Account> {
    return this.authenticationModule.login(credentials)
  }

  public logout(): Promise<void> {
    return this.authenticationModule.logout()
  }

  public refreshUser(): Promise<Account | undefined> {
    return new Promise((resolve, reject) => {
      const tokenHolder = Injector.get(TokenHolder)

      if (tokenHolder.ensureTokenIsValid()) {
        this.authenticationModule
          .fetchLogin()
          .then(resolve)
          .catch(reject)
      } else {
        resolve()
      }
    })
  }
}
